import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../RootState';
import {
    PROFILE_NOTICE_LASTNAME,
    PROFILE_NOTICE_NAME,
    PROFILE_NOTICE_NAME_AND_LASTNAME,
} from './shopping_cart.constants';

export const selectDataSuggested = createSelector(
    (state: RootState) => state.shopping_cart,
    (suggested) => suggested
);

export const selectCommercialCartSelection = createSelector(
    (state: RootState) => state.shopping_cart.suggested.selection,
    (selection) => selection
);

export const selectSelectedMonths = createSelector(
    (state: RootState) => state.shopping_cart.suggested.selection.selectedMonths,
    (selectedMonths) => selectedMonths
);

export const selectSelectedPaymentMethod = createSelector(
    (state: RootState) => state.shopping_cart.suggested.selection.selectedPaymentMethod,
    (selectedPaymentMethod) => selectedPaymentMethod
);

export const selectUserProfile = createSelector(
    (state: RootState) => state.shopping_cart.suggested.user_profile,
    (user_profile) => user_profile
);

export const selectSuggestedUserId = createSelector(
    (state: RootState) => state.shopping_cart.suggested.users.user_id,
    (user_id) => user_id
);

export const selectHasInputErrors = createSelector(
    (state: RootState) => state.shopping_cart.suggested.selection.inputErrorByProductId,
    (inputErrorByProductId) =>
        Object.values(inputErrorByProductId).some((e) => e.price || e.quantity)
);

export const selectHasLastname = createSelector(
    (state: RootState) => state.shopping_cart.suggested.user_profile,
    (user_profile) => Boolean(user_profile?.lastname?.trim())
);

export const selectHasName = createSelector(
    (state: RootState) => state.shopping_cart.suggested.user_profile,
    (user_profile) => Boolean(user_profile?.name?.trim())
);

/**
 * Mensaje único de perfil incompleto por falta de nombre y/o apellido.
 * Devuelve `undefined` cuando ambos están completos.
 * Centraliza la regla para reusarla en el carrito (bloqueo de pago) y en
 * la tab "Información del usuario" (aviso temprano).
 */
export const selectProfileCompletenessNotice = createSelector(
    (state: RootState) => state.shopping_cart.suggested.user_profile,
    (user_profile): string | undefined => {
        const hasName = Boolean(user_profile?.name?.trim());
        const hasLastname = Boolean(user_profile?.lastname?.trim());
        if (!hasName && !hasLastname) return PROFILE_NOTICE_NAME_AND_LASTNAME;
        if (!hasLastname) return PROFILE_NOTICE_LASTNAME;
        if (!hasName) return PROFILE_NOTICE_NAME;
        return undefined;
    }
);

export const selectCartTotal = createSelector(
    (state: RootState) => state.shopping_cart.suggested.products.category,
    (state: RootState) => state.shopping_cart.suggested.selection.selectedByProductId,
    (state: RootState) => state.shopping_cart.suggested.selection.priceByProductId,
    (category, selectedByProductId, priceByProductId) => {
        const allProducts = Object.values(category).flat();
        return allProducts
            .filter((p) => selectedByProductId[p.product_id])
            .reduce((sum, p) => sum + (priceByProductId[p.product_id] ?? 0), 0);
    }
);